import hasLocalStorage from "@mgdx/shared/src/utils/hasLocalStorage";
import {
  type PersistedSliceConfig,
  readPersistedSlice,
  subscribePersistedSlice,
} from "@mgdx/shared/src/utils/persistedSlice";
import type { StoreApi } from "zustand/vanilla";

/**
 * ブラウザでは保存済みの状態を同期で復元し、以後の変更を保存する。Gatsby の SSR では何もしない。
 * 初回の描画から保存済みの状態を使えるよう、store の生成直後にモジュールの読み込み時に呼ぶ。
 */
export const persistStore = <T extends object, S extends T>(
  store: StoreApi<S>,
  config: PersistedSliceConfig<T>,
  initialState: T
) => {
  if (typeof window === "undefined") return;

  const restored = readPersistedSlice(config, initialState);
  store.setState(restored as Partial<S>);
  subscribePersistedSlice(store, config, restored);
};

// ログアウトでは clearCurrentUser が初期状態(ログイン確認済み)を保存し直すため消さない。
// 消すと次の起動がログイン確認前から始まり、確認が終わるまでトップ画面が表示されない
const KEPT_PERSIST_KEYS = ["persist:currentUser"];

/**
 * localStorage に保存した状態(persist: を含むキー)を消す。sessionStorage の状態は消さない。
 */
export const clearPersistedState = () => {
  if (!hasLocalStorage()) return;

  Object.keys(localStorage)
    .filter((key) => key.includes("persist:") && !KEPT_PERSIST_KEYS.includes(key))
    .forEach((key) => localStorage.removeItem(key));
};
